import { prefersH264Encoding } from "./h264Hardware";

/**
 * Konkord: one encode of our stream for every direct viewer.
 *
 * Each direct viewer has its own connection, and the browser encodes the
 * picture once per connection: 6 viewers, 6 encodes (measured: 32% of a
 * core for 1 viewer, 178% for 6, at 1080p60 on the GPU). Here a WebCodecs
 * encoder encodes it once, and each connection sends a tiny stand-in track
 * whose encoded frames get the real ones put in their place (encoded
 * insertable streams). The viewers receive a normal H.264 stream.
 *
 * Chromium only (WebCodecs, MediaStreamTrackProcessor, createEncodedStreams);
 * elsewhere each connection encodes its own copy as before.
 */

/**
 * Konkord: a viewer whose oldest waiting frame is this old starts again at a
 * key frame (31/10: was 3 frames, so one hiccup of the whole computer
 * emptied every viewer's queue at once and the stream froze for everyone)
 */
const MAX_QUEUE_MS = 400;
/** …and never holds more frames than this */
const MAX_QUEUE = 30;
/** Key frames asked by viewers (picture loss), at most one this often */
const KEY_FRAME_MIN_MS = 1000;
/** Frames waiting in the encoder above this are dropped (it is behind) */
const MAX_ENCODE_QUEUE = 2;
/** Konkord: frames come in but none comes out this long: the encoder is stuck */
const STALL_MS = 2500;
/** Konkord: stuck again this soon after a restart: give up (one per viewer) */
const RESTART_WINDOW_MS = 30_000;
/** Size of the stand-in picture each connection encodes (next to nothing) */
const STAND_IN_WIDTH = 64;
const STAND_IN_HEIGHT = 36;

export function sharedEncodeSupported() {
  return (
    typeof VideoEncoder === "function" &&
    typeof MediaStreamTrackProcessor === "function" &&
    typeof RTCRtpSender !== "undefined" &&
    "createEncodedStreams" in RTCRtpSender.prototype &&
    prefersH264Encoding()
  );
}

type Chunk = { data: ArrayBuffer; key: boolean; at: number };

type Viewer = {
  /** Its connection started sending (frames before that would pile up) */
  ready: boolean;
  queue: Chunk[];
  paused: boolean;
  /** Nothing sent until a key frame (just joined, resumed or fell behind) */
  waitingKey: boolean;
  removed: boolean;
  /**
   * Konkord: its link can't carry the shared encode: its connection encodes
   * its own copy (adapted to that link), sent as it is
   */
  own: boolean;
  /**
   * Konkord: fell behind and its connection hasn't sent since: the key
   * frame is asked when it sends again (a stopped connection asking every
   * time made key frames for everyone, over and over)
   */
  stalled: boolean;
  /** Konkord: its own copy started (frames before its first key are dropped) */
  ownStarted: boolean;
};

export type SharedTarget = { height: number; fps: number; bitrate: number };

export type SharedStats = {
  width: number;
  height: number;
  fps: number;
  bitrate: number;
  /** Average time from a frame given to the encoder to its output */
  encodeMs: number;
  /** Frames dropped since the last stats because the encoder was behind */
  dropped: number;
  keyFrames: number;
  /** Konkord: viewers' queues that fell too far behind and started again */
  restarts: number;
};

/**
 * H.264 Constrained Baseline, the profile every browser decodes (Firefox
 * receives with OpenH264, Baseline only); level 5.1 covers 1440p60
 */
const CODECS = ["avc1.42e033", "avc1.42e02a", "avc1.42e01f"];

/** Chromium's MediaStreamTrackProcessor, typed for the page (the bundled
 * types only know the worker version) */
type TrackProcessor = new (init: { track: MediaStreamTrack }) => {
  readable: ReadableStream<VideoFrame>;
};

function framesOf(track: MediaStreamTrack) {
  const Processor = MediaStreamTrackProcessor as unknown as TrackProcessor;
  return new Processor({ track }).readable.getReader();
}

export class SharedEncoder {
  /** The stand-in track given to every direct connection */
  readonly track: MediaStreamTrack;

  #scaled: MediaStreamTrack;
  #encoder: VideoEncoder;
  #reader: ReadableStreamDefaultReader<VideoFrame>;
  #standIn: CanvasRenderingContext2D;
  #tick = 0;
  #viewers = new Set<Viewer>();
  #needKey = true;
  #lastKeyAsk = 0;
  #keyTimer?: ReturnType<typeof setTimeout>;
  #closed = false;
  #codec = CODECS[0];
  #target: SharedTarget;
  /** Konkord: what the encoder runs now (update compares with it) */
  #applied: SharedTarget = { height: 0, fps: 0, bitrate: 0 };
  #applying = false;
  #size = { width: 0, height: 0 };
  /** Konkord: one more stand-in frame asked, to carry waiting frames */
  #draining = false;
  /** Konkord: watch for an encoder that stopped (see #checkStall) */
  #watchdog: ReturnType<typeof setInterval>;
  #lastInput = 0;
  #lastOutput = performance.now();
  #restartedAt = -RESTART_WINDOW_MS;
  #restarts = 0;
  #onError: (error: unknown) => void;
  // stats
  #started = new Map<number, number>();
  #encodeTime = 0;
  #encodedFrames = 0;
  #encodedBytes = 0;
  #dropped = 0;
  #keyFrames = 0;
  #statsAt = performance.now();

  constructor(
    source: MediaStreamTrack,
    target: SharedTarget,
    onError: (error: unknown) => void,
  ) {
    this.#target = target;
    this.#onError = onError;

    // the browser scales and paces our own copy of the capture (on the GPU)
    this.#scaled = source.clone();

    const canvas = document.createElement("canvas");
    canvas.width = STAND_IN_WIDTH;
    canvas.height = STAND_IN_HEIGHT;
    this.#standIn = canvas.getContext("2d")!;
    this.track = canvas.captureStream(0).getVideoTracks()[0];

    this.#encoder = new VideoEncoder({
      output: (chunk) => this.#output(chunk),
      error: (error) => {
        if (this.#closed) return;
        this.close();
        this.#onError(error);
      },
    });

    this.#reader = framesOf(this.#scaled);
    this.#watchdog = setInterval(() => this.#checkStall(), 1000);
    void this.#start();
  }

  async #start() {
    try {
      await this.#reconfigure(true);
    } catch (error) {
      this.close();
      this.#onError(error);
      return;
    }
    void this.#pump();
  }

  /** Konkord: one configure at a time (update asks every couple of seconds) */
  async #reconfigure(resize: boolean) {
    this.#applying = true;
    try {
      await this.#applyTarget(resize);
    } finally {
      this.#applying = false;
    }
  }

  /**
   * Size, frame rate and bitrate for every viewer: the biggest size any of
   * them shows, the bitrate the slowest link carries
   */
  async update(target: SharedTarget) {
    this.#target = target;
    // the running configure picks the newest target up on the next update
    if (this.#applying) return;
    const applied = this.#applied;
    // Konkord: compared with what the encoder runs, not with the last ask
    // (small drops one after another never reached it); a drop applies
    // right away (the link can't carry more), a rise when worth it
    const bitrateChanged =
      target.bitrate < applied.bitrate * 0.92 ||
      target.bitrate > applied.bitrate * 1.15;
    if (
      target.height !== applied.height ||
      target.fps !== applied.fps ||
      bitrateChanged
    ) {
      await this.#reconfigure(target.height !== applied.height).catch(
        (error: unknown) => {
          this.close();
          this.#onError(error);
        },
      );
    }
  }

  async #applyTarget(resize: boolean) {
    if (this.#closed) return;
    // Konkord: one target for the whole configure (update may change
    // #target meanwhile: the next update applies that)
    const target = { ...this.#target };
    const { height, fps } = target;
    if (resize || !this.#size.height) {
      await this.#scaled
        .applyConstraints({ height: { max: height }, frameRate: { max: fps } })
        .catch(() => {});
    }
    const settings = this.#scaled.getSettings();
    // even sizes, as H.264 needs
    const outHeight = Math.max(
      2,
      Math.floor((settings.height ?? height) / 2) * 2,
    );
    const outWidth = Math.max(
      2,
      Math.floor(
        ((settings.width ?? (height * 16) / 9) * outHeight) /
          (settings.height ?? height) /
          2,
      ) * 2,
    );

    // Konkord: the graphics card first, else the processor: Chromium takes
    // "prefer-hardware" as hardware only, so a computer without an H.264
    // encoder on its card (Linux, 01/11) had no shared encode and fell back
    // to one encode per viewer, 2 viewers at most on the processor (a
    // camera reached 1 of 7). One encode on the processor still beats one
    // per viewer
    const tries = CODECS.flatMap((codec) =>
      (["prefer-hardware", "no-preference"] as const).map(
        (hardwareAcceleration) => ({ codec, hardwareAcceleration }),
      ),
    ).sort((a, b) =>
      a.hardwareAcceleration === b.hardwareAcceleration
        ? 0
        : a.hardwareAcceleration === "prefer-hardware"
          ? -1
          : 1,
    );
    for (const { codec, hardwareAcceleration } of tries) {
      const config: VideoEncoderConfig = {
        codec,
        width: outWidth,
        height: outHeight,
        bitrate: target.bitrate,
        framerate: fps,
        hardwareAcceleration,
        latencyMode: "realtime",
        avc: { format: "annexb" },
      };
      const support = await VideoEncoder.isConfigSupported(config);
      if (!support.supported) continue;
      if (this.#closed) return;
      if (
        codec !== this.#codec ||
        outWidth !== this.#size.width ||
        outHeight !== this.#size.height
      )
        this.#needKey = true;
      this.#codec = codec;
      this.#size = { width: outWidth, height: outHeight };
      this.#encoder.configure(config);
      this.#applied = target;
      return;
    }
    throw new Error("no H.264 encoder for the shared stream");
  }

  async #pump() {
    const reader = this.#reader;
    for (;;) {
      let result: ReadableStreamReadResult<VideoFrame>;
      try {
        result = await reader.read();
      } catch {
        return;
      }
      // a new source took over (setSource): its own pump runs
      if (result.done) return;
      const frame = result.value;
      if (this.#closed || reader !== this.#reader) {
        frame.close();
        return;
      }
      // nobody to send to (all paused, or all on their own copy): nothing
      // to encode
      const active = [...this.#viewers].some(
        (viewer) => !viewer.paused && !viewer.own,
      );
      if (!active || this.#encoder.state !== "configured") {
        frame.close();
        continue;
      }
      const now = performance.now();
      // Konkord: frames again after a pause: the encoder has had no chance
      // to answer yet (not stuck)
      if (now - this.#lastInput > 1000) this.#lastOutput = now;
      this.#lastInput = now;
      if (this.#encoder.encodeQueueSize > MAX_ENCODE_QUEUE) {
        this.#dropped++;
        frame.close();
        continue;
      }
      this.#started.set(frame.timestamp, performance.now());
      try {
        this.#encoder.encode(frame, { keyFrame: this.#needKey });
        if (this.#needKey) this.#keyFrames++;
        this.#needKey = false;
      } catch {
        // reconfiguring: the next frame goes
      }
      frame.close();
    }
  }

  #output(chunk: EncodedVideoChunk) {
    const started = this.#started.get(chunk.timestamp);
    if (started !== undefined) {
      this.#encodeTime += performance.now() - started;
      this.#started.delete(chunk.timestamp);
    }
    if (this.#started.size > 120) this.#started.clear();
    this.#encodedFrames++;
    this.#encodedBytes += chunk.byteLength;
    const now = performance.now();
    this.#lastOutput = now;

    const data = new ArrayBuffer(chunk.byteLength);
    chunk.copyTo(data);
    const key = chunk.type === "key";
    for (const viewer of this.#viewers) {
      if (viewer.paused || !viewer.ready || viewer.own) continue;
      if (viewer.waitingKey) {
        if (!key) continue;
        viewer.waitingKey = false;
      }
      // Konkord: a key frame makes what waits before it useless
      if (key) viewer.queue.length = 0;
      viewer.queue.push({ data, key, at: now });
      // fell behind (slow link or frames held back): start again at a key
      // frame instead of sending a picture that can't be decoded
      if (
        now - viewer.queue[0].at > MAX_QUEUE_MS ||
        viewer.queue.length > MAX_QUEUE
      ) {
        viewer.queue.length = 0;
        viewer.waitingKey = true;
        this.#restarts++;
        viewer.stalled = true;
      }
    }
    // one stand-in frame per real one: each connection encodes it (tiny)
    // and its encoded frame carries the real one
    this.#paintStandIn();
  }

  #paintStandIn() {
    this.#standIn.fillStyle = this.#tick++ % 2 ? "#000" : "#010101";
    this.#standIn.fillRect(0, 0, STAND_IN_WIDTH, STAND_IN_HEIGHT);
    (this.track as CanvasCaptureMediaStreamTrack).requestFrame();
  }

  /**
   * Konkord: a viewer still has real frames waiting (a stand-in frame got
   * lost on its connection): one more stand-in soon carries them, instead
   * of the queue only ever growing (connections without any just drop it)
   */
  #drain() {
    if (this.#draining || this.#closed) return;
    this.#draining = true;
    setTimeout(() => {
      this.#draining = false;
      if (!this.#closed) this.#paintStandIn();
    }, 0);
  }

  /**
   * Konkord: frames come in but the encoder gives nothing back: restart it
   * once; stuck again soon after, give up (each viewer then gets its own
   * encode, see the error handler)
   */
  #checkStall() {
    if (this.#closed) return;
    if ([...this.#viewers].some((viewer) => viewer.queue.length)) this.#drain();
    if (this.#applying) return;
    const now = performance.now();
    const feeding = [...this.#viewers].some(
      (viewer) => !viewer.paused && !viewer.own,
    );
    if (
      !feeding ||
      now - this.#lastInput > 1000 ||
      now - this.#lastOutput < STALL_MS
    )
      return;
    if (now - this.#restartedAt < RESTART_WINDOW_MS) {
      this.close();
      this.#onError(new Error("shared encode stuck"));
      return;
    }
    console.warn("[konkord] shared encode stuck, restarting it");
    this.#restartedAt = now;
    this.#lastOutput = now;
    try {
      this.#encoder.reset();
    } catch {
      // closed meanwhile
    }
    this.#needKey = true;
    this.#size = { width: 0, height: 0 };
    void this.#reconfigure(true).catch((error: unknown) => {
      this.close();
      this.#onError(error);
    });
  }

  /**
   * A key frame for everyone soon (a viewer joined, resumed or lost
   * pictures)
   */
  requestKeyFrame() {
    const now = performance.now();
    const wait = this.#lastKeyAsk + KEY_FRAME_MIN_MS - now;
    // asked again too soon: one later, so nobody waits for good
    if (wait > 0) {
      this.#keyTimer ??= setTimeout(() => {
        this.#keyTimer = undefined;
        this.requestKeyFrame();
      }, wait);
      return;
    }
    this.#lastKeyAsk = now;
    this.#needKey = true;
  }

  /**
   * Our screen share got new pictures (another screen or window)
   */
  setSource(source: MediaStreamTrack) {
    if (this.#closed) return;
    void this.#reader.cancel().catch(() => {});
    this.#scaled.stop();
    this.#scaled = source.clone();
    this.#reader = framesOf(this.#scaled);
    this.#needKey = true;
    void this.#reconfigure(true)
      .then(() => this.#pump())
      .catch((error: unknown) => {
        this.close();
        this.#onError(error);
      });
  }

  /**
   * Send through this connection's sender (made on a connection created
   * with encodedInsertableStreams); returns the controls for this viewer
   */
  attach(sender: RTCRtpSender) {
    const viewer: Viewer = {
      ready: false,
      queue: [],
      paused: false,
      waitingKey: true,
      removed: false,
      own: false,
      stalled: false,
      ownStarted: false,
    };
    this.#viewers.add(viewer);

    const { readable, writable } = (
      sender as RTCRtpSender & {
        createEncodedStreams(): {
          readable: ReadableStream<RTCEncodedVideoFrame>;
          writable: WritableStream<RTCEncodedVideoFrame>;
        };
      }
    ).createEncodedStreams();
    let first = true;
    void readable
      .pipeThrough(
        new TransformStream<RTCEncodedVideoFrame, RTCEncodedVideoFrame>({
          transform: (frame, controller) => {
            if (viewer.removed) return;
            // Konkord: its own copy (slow link), sent as the connection
            // encoded it, from its first key frame (stand-ins already on
            // their way are dropped)
            if (viewer.own) {
              if (!viewer.ownStarted) {
                const width = frame.getMetadata?.().width ?? 0;
                if (frame.type !== "key" || width <= STAND_IN_WIDTH) return;
                viewer.ownStarted = true;
              }
              return controller.enqueue(frame);
            }
            // the first frame through: the connection sends now, start it
            // at a key frame
            if (first) {
              first = false;
              viewer.ready = true;
              viewer.waitingKey = true;
              viewer.queue.length = 0;
              this.requestKeyFrame();
            } else if (frame.type === "key" || viewer.stalled) {
              // Konkord: a stand-in key frame later: the viewer lost
              // pictures and asked for one; the real one starts its queue
              // again when it comes (nothing waiting is thrown away now).
              // Or its connection sends again after falling behind
              viewer.stalled = false;
              this.requestKeyFrame();
            }
            const next = viewer.queue.shift();
            if (!next) return;
            frame.data = next.data;
            controller.enqueue(frame);
            if (viewer.queue.length) this.#drain();
          },
        }),
      )
      .pipeTo(writable)
      .catch(() => {});

    return {
      setPaused: (paused: boolean) => {
        if (viewer.paused === paused) return;
        viewer.paused = paused;
        viewer.queue.length = 0;
        if (!paused && !viewer.own) {
          viewer.waitingKey = true;
          this.requestKeyFrame();
        }
      },
      /**
       * Konkord: its connection encodes its own copy (true) or carries the
       * shared one again (false)
       */
      setOwn: (own: boolean) => {
        if (viewer.own === own) return;
        viewer.own = own;
        viewer.ownStarted = false;
        viewer.stalled = false;
        viewer.queue.length = 0;
        if (!own) {
          viewer.waitingKey = true;
          this.requestKeyFrame();
        }
      },
      detach: () => {
        viewer.removed = true;
        viewer.queue.length = 0;
        this.#viewers.delete(viewer);
      },
    };
  }

  get viewers() {
    return this.#viewers.size;
  }

  /**
   * What the encoder made since the last call
   */
  stats(): SharedStats {
    const now = performance.now();
    const seconds = (now - this.#statsAt) / 1000 || 1;
    const result = {
      width: this.#size.width,
      height: this.#size.height,
      fps: Math.round(this.#encodedFrames / seconds),
      bitrate: Math.round((this.#encodedBytes * 8) / seconds),
      encodeMs: this.#encodedFrames
        ? this.#encodeTime / this.#encodedFrames
        : 0,
      dropped: this.#dropped,
      keyFrames: this.#keyFrames,
      restarts: this.#restarts,
    };
    this.#statsAt = now;
    this.#encodedFrames = 0;
    this.#encodedBytes = 0;
    this.#encodeTime = 0;
    this.#dropped = 0;
    this.#keyFrames = 0;
    this.#restarts = 0;
    return result;
  }

  close() {
    if (this.#closed) return;
    this.#closed = true;
    clearTimeout(this.#keyTimer);
    clearInterval(this.#watchdog);
    for (const viewer of this.#viewers) {
      viewer.removed = true;
      viewer.queue.length = 0;
    }
    this.#viewers.clear();
    void this.#reader.cancel().catch(() => {});
    this.#scaled.stop();
    this.track.stop();
    if (this.#encoder.state !== "closed") this.#encoder.close();
  }
}
